<template>
  <g
    class="line-annotation"
    :class="{ selected, editing }"
  >
    <!-- Ligne principale (draggable si sélectionnée) -->
    <line
      :x1="annotation.data.x1"
      :y1="annotation.data.y1"
      :x2="annotation.data.x2"
      :y2="annotation.data.y2"
      :stroke="selected ? '#4a9eff' : (annotation.color || '#ff6b6b')"
      :stroke-width="selected ? (strokeWidth + 1) : strokeWidth"
      stroke-linecap="round"
      class="line-main"
      :class="{ draggable: selected }"
      @click.stop="handleClick"
      @mousedown.stop="selected ? handleStartLineDrag($event) : null"
    />

    <!-- Points de contrôle (visibles si sélectionné) -->
    <g v-if="selected">
      <!-- Point de départ -->
      <circle
        :cx="annotation.data.x1"
        :cy="annotation.data.y1"
        :r="handleRadius"
        fill="#4a9eff"
        stroke="white"
        :stroke-width="handleStroke"
        class="control-point"
        @mousedown.stop="handleStartDrag($event, 'start')"
      />
      <!-- Point d'arrivée -->
      <circle
        :cx="annotation.data.x2"
        :cy="annotation.data.y2"
        :r="handleRadius"
        fill="#4a9eff"
        stroke="white"
        :stroke-width="handleStroke"
        class="control-point"
        @mousedown.stop="handleStartDrag($event, 'end')"
      />
    </g>
  </g>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import type { Annotation } from '@api/annotations.api';
import {
  ANNOTATION_DEFAULT_STROKE_PX,
  ANNOTATION_HANDLE_RADIUS_PX,
  ANNOTATION_HANDLE_STROKE_PX,
} from '@/constants/annotations';

const props = defineProps<{
  annotation: Annotation;
  selected: boolean;
  svgRef?: SVGSVGElement | null;
  canvasPixelsPerUser?: number;
}>();

const emit = defineEmits<{
  select: [annotation: Annotation];
  update: [annotation: Annotation, data: any, save?: boolean];
  delete: [annotation: Annotation];
}>();

const editing = ref(false);
const dragging = ref(false);
const dragPoint = ref<'start' | 'end' | 'line' | null>(null);
const dragStartPos = ref({ x: 0, y: 0 });
const dragStartLine = ref({ x1: 0, y1: 0, x2: 0, y2: 0 });
const lastDraggedData = ref<any | null>(null);

const pixelsPerUser = computed(() => Math.max(props.canvasPixelsPerUser ?? 1, 0.01));
const strokeWidth = computed(() => props.annotation.stroke_width ?? ANNOTATION_DEFAULT_STROKE_PX);
const handleRadius = computed(() => ANNOTATION_HANDLE_RADIUS_PX / pixelsPerUser.value);
const handleStroke = computed(() => ANNOTATION_HANDLE_STROKE_PX / pixelsPerUser.value);

const handleClick = () => {
  if (!props.selected) {
    emit('select', props.annotation);
  }
};

// Fonction pour snapper à un angle multiple de 45°
const snapToAngle = (x1: number, y1: number, x2: number, y2: number): { x: number; y: number } => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const distance = Math.sqrt(dx * dx + dy * dy);
  
  // Calculer l'angle actuel
  let angle = Math.atan2(dy, dx) * (180 / Math.PI);
  
  // Snapper à l'angle multiple de 45° le plus proche
  const snapAngle = Math.round(angle / 45) * 45;
  
  // Convertir l'angle snappé en coordonnées
  const radians = snapAngle * (Math.PI / 180);
  return {
    x: x1 + distance * Math.cos(radians),
    y: y1 + distance * Math.sin(radians),
  };
};

const handleStartDrag = (event: MouseEvent, point: 'start' | 'end') => {
  if (!props.svgRef) return;
  
  dragging.value = true;
  dragPoint.value = point;
  lastDraggedData.value = null;
  event.preventDefault();
  event.stopPropagation();
};

const handleStartLineDrag = (event: MouseEvent) => {
  if (!props.svgRef) return;
  
  dragging.value = true;
  dragPoint.value = 'line';
  lastDraggedData.value = null;
  
  const svg = props.svgRef;
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
  
  dragStartPos.value = { x: svgP.x, y: svgP.y };
  dragStartLine.value = {
    x1: props.annotation.data.x1,
    y1: props.annotation.data.y1,
    x2: props.annotation.data.x2,
    y2: props.annotation.data.y2,
  };
  
  event.preventDefault();
  event.stopPropagation();
};

const handleDragMove = (event: MouseEvent) => {
  if (!dragging.value || !dragPoint.value || !props.svgRef) return;
  
  const svg = props.svgRef;
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
  
  const newData = { ...props.annotation.data };
  
  if (dragPoint.value === 'line') {
    // Déplacer toute la ligne
    const deltaX = svgP.x - dragStartPos.value.x;
    const deltaY = svgP.y - dragStartPos.value.y;
    
    newData.x1 = dragStartLine.value.x1 + deltaX;
    newData.y1 = dragStartLine.value.y1 + deltaY;
    newData.x2 = dragStartLine.value.x2 + deltaX;
    newData.y2 = dragStartLine.value.y2 + deltaY;
  } else if (dragPoint.value === 'start') {
    // Déplacer le point de départ
    if (event.shiftKey) {
      // Snapper à un angle multiple de 45°
      const snapped = snapToAngle(props.annotation.data.x2, props.annotation.data.y2, svgP.x, svgP.y);
      newData.x1 = snapped.x;
      newData.y1 = snapped.y;
    } else {
      newData.x1 = svgP.x;
      newData.y1 = svgP.y;
    }
  } else if (dragPoint.value === 'end') {
    // Déplacer le point d'arrivée
    if (event.shiftKey) {
      // Snapper à un angle multiple de 45°
      const snapped = snapToAngle(props.annotation.data.x1, props.annotation.data.y1, svgP.x, svgP.y);
      newData.x2 = snapped.x;
      newData.y2 = snapped.y;
    } else {
      newData.x2 = svgP.x;
      newData.y2 = svgP.y;
    }
  }
  
  lastDraggedData.value = newData;
  emit('update', props.annotation, newData, false);
};

const handleDragEnd = () => {
  if (dragging.value) {
    const finalData = lastDraggedData.value;
    dragging.value = false;
    dragPoint.value = null;
    lastDraggedData.value = null;
    if (finalData) {
      emit('update', props.annotation, finalData, true);
    }
  }
};

onMounted(() => {
  window.addEventListener('mousemove', handleDragMove);
  window.addEventListener('mouseup', handleDragEnd);
});

onUnmounted(() => {
  window.removeEventListener('mousemove', handleDragMove);
  window.removeEventListener('mouseup', handleDragEnd);
});
</script>

<style scoped>
.line-annotation {
  cursor: pointer;
}

.line-main {
  transition: stroke 0.2s, stroke-width 0.2s;
}

.line-main:hover {
  opacity: 0.92;
}

.line-main.draggable {
  cursor: move;
}

.control-point {
  cursor: move;
}
</style>
